import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI, assessmentsAPI, intelligenceAPI, candidatesAPI } from '../lib/api';
import { SafeHtml } from '../components/SafeHtml';
import { useJobPolling } from '../hooks/useJobPolling';
import VerificationSendDialog from '../components/VerificationSendDialog';
import { UploadTracker } from '../components/job/UploadTracker';
import { EditJobDialog } from '../components/job/EditJobDialog';
import { ScoringInsightsDialog } from '../components/job/ScoringInsightsDialog';
import { DistributeDialog } from '../components/job/DistributeDialog';
import { JobHeader } from '../components/job/JobHeader';
import { JDQualityCard } from '../components/job/JDQualityCard';
import { CandidateList } from '../components/job/CandidateList';
import BehavioralFitPanel from '../components/behavioral/BehavioralFitPanel';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    job, candidates, loading, uploading, uploadFiles,
    loadData, loadCandidates, dismissTracker,
    getRootProps, getInputProps, isDragActive,
  } = useJobPolling(jobId);

  const [showEdit, setShowEdit] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [rescoring, setRescoring] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showVerify, setShowVerify] = useState(null); // candidate to verify

  const handleParseLead = useCallback((c) => {
    candidatesAPI.reparse(c._id)
      .then(() => { toast.success(`Parsing & scoring ${c.name}…`); loadCandidates(); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to start parsing'));
  }, [loadCandidates]);

  const handleRescore = async () => {
    setRescoring(true);
    try {
      await jobsAPI.rescore(jobId);
      toast.success('Re-scoring all candidates...');
      loadCandidates();
    } catch (err) {
      toast.error('Re-scoring failed');
    } finally {
      setRescoring(false);
    }
  };

  const handleRetryStuck = async () => {
    setRetrying(true);
    try {
      const res = await jobsAPI.retryStuck(jobId);
      if (res.data.retried > 0) toast.success(res.data.message);
      else toast.info(res.data.message);
      loadCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry stuck resumes');
    } finally {
      setRetrying(false);
    }
  };

  const csvField = (value) => {
    let s = String(value ?? '');
    // Neutralize leading formula characters to prevent spreadsheet formula injection.
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleExport = async () => {
    try {
      const res = await jobsAPI.exportCSV(jobId);
      const { job_title, candidates: rows } = res.data;
      const headers = ['Name', 'Email', 'Score', 'Fit', 'Matched Skills', 'Missing Skills', 'Strengths', 'Summary'];
      const csv = [headers.join(','), ...rows.map(r =>
        [r.name, r.email, r.score, r.fit, r.matched_skills, r.missing_skills, r.strengths, r.summary].map(csvField).join(',')
      )].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job_title.replace(/\s+/g, '_')}_candidates.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleEditJob = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...editForm,
        required_skills: typeof editForm.required_skills === 'string'
          ? editForm.required_skills.split(',').map(s => s.trim()).filter(Boolean)
          : editForm.required_skills,
        nice_to_have_skills: typeof editForm.nice_to_have_skills === 'string'
          ? editForm.nice_to_have_skills.split(',').map(s => s.trim()).filter(Boolean)
          : editForm.nice_to_have_skills
      };
      await jobsAPI.update(jobId, updateData);
      toast.success('Job updated');
      setShowEdit(false);
      loadData();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const handleSendAssessment = useCallback((c) => {
    const email = c.parsed_resume?.email || '';
    assessmentsAPI.sendToCandidate(jobId, { candidate_name: c.name, candidate_email: email })
      .then(res => {
        toast.success(`Assessment sent to ${c.name}! Link: ${res.data.assessment_link}`);
        navigator.clipboard?.writeText(`${window.location.origin}${res.data.assessment_link}`).catch(() => {});
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to send assessment'));
  }, [jobId]);

  const handleAdaptiveInterview = async () => {
    try {
      const res = await intelligenceAPI.createAdaptiveInterview(jobId);
      const link = `${window.location.origin}/join-interview/${res.data.access_code}`;
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.success(`Adaptive interview created! Link copied. (~${res.data.estimated_minutes || 8} min)`);
    } catch (err) {
      toast.error('Failed to create adaptive interview');
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'hsl(var(--muted-foreground))' }}>Loading job details...</div>;
  }
  if (!job) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'hsl(var(--muted-foreground))' }}>Job not found</div>;
  }

  const completedCount = candidates.filter(c => c.status === 'completed').length;
  const allTrackerDone = uploadFiles.length > 0 && uploadFiles.every(f => f.status === 'completed' || f.status === 'error');

  // "Stuck" = errored, or in a processing state that hasn't advanced for ~90s.
  // (updated_at is a naive UTC ISO string from the backend — treat it as UTC.)
  const parseTs = (s) => { if (!s) return 0; return new Date(/[Z+]/.test(s) ? s : `${s}Z`).getTime(); };
  const stuckCount = candidates.filter(c =>
    c.status === 'error' ||
    (['uploaded', 'parsing', 'matching'].includes(c.status) && (Date.now() - parseTs(c.updated_at)) > 90000)
  ).length;

  return (
    <div data-testid="job-detail-page">
      <JobHeader
        job={job}
        candidatesCount={candidates.length}
        navigate={navigate}
        actions={{
          jobId, navigate,
          onSource: () => navigate(`/sourcing?job=${jobId}`),
          onEdit: () => { setEditForm({ ...job, required_skills: (job.required_skills || []).join(', '), nice_to_have_skills: (job.nice_to_have_skills || []).join(', ') }); setShowEdit(true); },
          onShowInsights: () => setShowInsights(true),
          onRescore: handleRescore,
          onRetryStuck: handleRetryStuck,
          onExport: handleExport,
          onAdaptiveInterview: handleAdaptiveInterview,
          onDistribute: () => setShowDistribute(true),
          rescoring,
          retrying,
          stuckCount,
          candidatesCount: candidates.length,
          completedCount,
        }}
      />

      <div className="job-detail-grid">
        {/* Left: Processing pipeline + Candidates list */}
        <CandidateList
          candidates={candidates}
          navigate={navigate}
          onSendAssessment={handleSendAssessment}
          onVerify={(cand) => setShowVerify(cand)}
          onParseLead={handleParseLead}
          jobId={jobId}
        />

        {/* Right: Upload + tracker + Job Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Upload Resumes</h3>
            <div {...getRootProps()} className={`upload-dropzone ${isDragActive ? 'active' : ''}`} data-testid="resume-upload-dropzone">
              <input {...getInputProps()} data-testid="resume-upload-input" />
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 0.75rem' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem', color: '#002A38' }}>
                {isDragActive ? 'Drop files here...' : uploading ? 'Uploading...' : 'Drop resumes here or click to browse'}
              </p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>PDF, DOCX, TXT &middot; Multiple files supported</p>
            </div>
          </Card>

          <UploadTracker files={uploadFiles} />

          {allTrackerDone && (
            <button
              data-testid="dismiss-tracker"
              onClick={dismissTracker}
              style={{
                background: 'none', border: '1px solid hsl(var(--border))',
                borderRadius: 10, padding: '0.5rem', cursor: 'pointer',
                fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))',
                marginTop: '-0.5rem', textAlign: 'center', transition: 'border-color 0.15s',
              }}
            >
              Dismiss upload tracker
            </button>
          )}

          <JDQualityCard job={job} />

          {/* Job Info */}
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Job Details</h3>
            <SafeHtml className="rte-content" data-testid="job-detail-description" html={job.description}
              style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6, maxHeight: 200, overflow: 'auto' }} />
            {(job.required_skills || []).length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Required Skills</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {job.required_skills.map((skill) => (
                    <span key={skill} style={{ padding: '0.1875rem 0.5rem', borderRadius: 999, background: 'rgba(240, 80, 0, 0.1)', border: '1px solid rgba(240, 80, 0, 0.2)', fontSize: '0.6875rem', color: '#0076A3' }}>{skill}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }} data-testid="job-behavioral-section">
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.15rem', fontWeight: 700, color: '#002A38', marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2"><path d="M12 2a4.5 4.5 0 0 0-4.5 4.5c0 .6.12 1.17.33 1.7A3.5 3.5 0 0 0 6 11.5a3.5 3.5 0 0 0 1.5 2.87A3.5 3.5 0 0 0 11 21a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z"/><path d="M12 2a4.5 4.5 0 0 1 4.5 4.5c0 .6-.12 1.17-.33 1.7A3.5 3.5 0 0 1 18 11.5a3.5 3.5 0 0 1-1.5 2.87A3.5 3.5 0 0 1 13 21a1 1 0 0 1-1-1"/></svg>
          Psychometric Fit &amp; Team Chemistry
        </h2>
        <BehavioralFitPanel jobId={jobId} />
      </div>

      <EditJobDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        editForm={editForm}
        setEditForm={setEditForm}
        onSubmit={handleEditJob}
      />

      <VerificationSendDialog
        open={!!showVerify}
        onClose={() => setShowVerify(null)}
        candidate={showVerify}
        jobId={jobId}
      />

      <ScoringInsightsDialog
        open={showInsights}
        onOpenChange={setShowInsights}
        jobId={jobId}
      />

      <DistributeDialog
        open={showDistribute}
        onOpenChange={setShowDistribute}
        job={job}
      />
    </div>
  );
}
